// deps
import React, { ReactNode, useContext, ReactElement, useState, useCallback, useMemo, useEffect } from "react";

// internal deps
import { useEffectOnce } from "@swiggy-private/core-ui";
import {
    CheckoutContext,
    CheckoutRoute,
    CheckoutActionTypes,
    AnalyticsContext,
    VpaTimerActionPayload,
    getBridgeSdk,
    useCustomHardwareBack,
    recordNREvent,
    NEWRELIC_TABLES,
    SetDataSource,
} from "@swiggy-private/checkout-core";

// context
import { PaymentPayContext } from "@payment-client/contexts/PaymentPayContext";
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";

// components
import CommonHeaderContent from "@payment-client/components/common-header-content/CommonHeaderContent";
import CommonUIWrapper from "@payment-client/components/common-ui-wrapper/CommonUIWrapper";
import { UpiTimer } from "@swiggy-private/payment-ui";

// constants
import { notAllowedWebviewRefreshVersionCodes } from "@payment-client/constants/WebviewRefresh";

// hooks
import usePollingTxnStatus from "@payment-client/hooks/UsePollingTxnStatus";
import { RouteProps } from "@payment-client/interfaces/Router";

// helpers
import {
    getPaymentHeaderSubtitleForSubRoutesV4,
    getPaymentCodeFromOrderData,
} from "@payment-client/helpers/PaymentHelper";
import { getItemCountAndAnnotation } from "@payment-client/helpers/CheckoutMetaHelper";
import { clearWebviewRefreshSavedState, getVersionCode, isBusinessLineSwiggyMoney } from "@payment-client/helpers/Misc";
import { setPersistOrderDataInLocalStorage } from "@swiggy-private/checkout-core";
import { getPollingConfiguration } from "@payment-client/helpers/PollingHelpers";

// analytics
import {
    upiTimerImpressionEvent,
    upiTimerBackClickEvent,
    upiTimerCancelClickEvent,
    upiTimerCancelHalfCardCancelClickEvent,
    upiTimerCancelHalfCardImpressionEvent,
} from "@payment-client/analytics/events/UpiTimer";

import styles from "./styles.v4.scss";
//defs
import { PAYMENT_CODES } from "@swiggy-private/payment-types";

export const TIMER_WIDGET_RADIUS = 120;
export const TIMER_WIDGET_STROKE_WIDTH = 10;

export const TIMER_WIDGET_RADIUS_V4 = 140;
export const TIMER_WIDGET_STROKE_WIDTH_V4 = 7;

const VpaTimerWrapper = function VpaTimerWrapper(props: { children: ReactNode; onBack: VoidFunction }): ReactElement {
    const { checkoutMetaInfo, paymentResponse } = useContext(ShowPaymentContext);
    const { cartItemsCount, addressAnnotation } = useMemo(
        () => getItemCountAndAnnotation(checkoutMetaInfo),
        [checkoutMetaInfo],
    );
    const { amountToPay } = useContext(PaymentPayContext);

    const onBack = props.onBack;

    const subtitleText = useMemo(() => {
        return getPaymentHeaderSubtitleForSubRoutesV4({
            selectedAddressAnnotation: addressAnnotation,
            cartAmount: String(amountToPay),
            itemCount: cartItemsCount,
            presentationDetails: paymentResponse?.presentationDetails,
        });
    }, [addressAnnotation, amountToPay, cartItemsCount, paymentResponse]);

    const header = <CommonHeaderContent title={"PAYMENT DETAILS"} subtitle={subtitleText} grayscale />;
    return (
        <CommonUIWrapper
            onBack={onBack}
            header={header}
            content={props.children}
            containerCSSClass={styles.vpaContainer}
        />
    );
};

export function VpaTimer(_: RouteProps): ReactElement | null {
    const [showCancelModal, setShowCancelModal] = useState(false);

    const { checkoutState, setCheckoutState } = useContext(CheckoutContext);
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const upiCollectStartTime = (checkoutState.payload as VpaTimerActionPayload)?.upiCollectStartTime;
    //Null Check for upiCollectStartTime is added due to following sentry error
    //https://sentry.io/organizations/bundl-technologies-pvt-ltd/issues/3772061531/?project=2772639&query=is%3Aunresolved&referrer=issue-stream
    const elapsedTime = upiCollectStartTime ? Date.now() - upiCollectStartTime : 0;

    const paymentCode = getPaymentCodeFromOrderData(checkoutState.payload?.placeOrderData);
    const { pollingIntervalInMs, pollingDurationInMs } = getPollingConfiguration(
        paymentCode || "",
        checkoutState?.payload?.order,
    );

    const txnStatusStop = usePollingTxnStatus({
        pollingDurationInMs,
        pollingIntervalInMs,
        startTime: upiCollectStartTime,
        paymentCode: paymentCode || "",
    });

    const onBack = useCallback((): void => {
        upiTimerBackClickEvent(clickEvent);
        setShowCancelModal(true);
    }, [clickEvent]);

    const onClickCancel = useCallback(
        (show: boolean): void => {
            setShowCancelModal(show);

            if (show) {
                upiTimerCancelClickEvent(clickEvent);
            }
        },
        [setShowCancelModal, clickEvent],
    );

    const onCancelTransaction = (): void => {
        if (checkoutState.type === CheckoutActionTypes.VPA_TIMER) {
            txnStatusStop();
            upiTimerCancelHalfCardCancelClickEvent(clickEvent);

            clearWebviewRefreshSavedState();

            setCheckoutState({
                type: CheckoutActionTypes.PROCESS_ORDER,
                payload: {
                    placeOrderData: checkoutState.payload.placeOrderData,
                    order: checkoutState.payload.order,
                },
            });
        }
    };

    const [itemCount, orderTotal] = useMemo(() => {
        if (checkoutState.type !== CheckoutActionTypes.VPA_TIMER) {
            return [0, 0];
        }

        const {
            order: { orderItems = [], orderTotal },
        } = checkoutState.payload;

        return [orderItems.reduce((acc, cur) => acc + Number(cur.quantity), 0), orderTotal];
    }, [checkoutState.payload, checkoutState.type]);

    useEffectOnce(() => {
        upiTimerImpressionEvent(impressionEvent);
        const versionCode = getVersionCode();

        try {
            const {
                order: { orderId, paymentTxnId },
            } = checkoutState.payload as VpaTimerActionPayload;

            // TODO: remove this temporary debug log once debugging is concluded for IM duplicate order issue
            recordNREvent({
                eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                name: "vpaTimerLoad",
                paymentTxnId: paymentTxnId || "",
                orderId: orderId || "",
            });
        } catch {
            /* No-op */
        }
        if (!isBusinessLineSwiggyMoney() && !notAllowedWebviewRefreshVersionCodes.includes(versionCode)) {
            const payload = {
                state: CheckoutActionTypes.VPA_TIMER,
                infoObject: {
                    ...checkoutState.payload,
                },
                paymentCode: PAYMENT_CODES.UPICOLLECT,
            };
            try {
                setPersistOrderDataInLocalStorage(payload, SetDataSource.VPA_TIMER);
            } catch (_) {
                // no-op
            }
            getBridgeSdk()
                .saveUsersLastState(payload)
                .catch(() => void 0);
        }
    });

    useCustomHardwareBack([CheckoutRoute.VPA_TIMER], onBack);

    useEffect(() => {
        if (showCancelModal) {
            upiTimerCancelHalfCardImpressionEvent(impressionEvent);
        }
    }, [showCancelModal, impressionEvent]);

    if (checkoutState.type !== CheckoutActionTypes.VPA_TIMER) {
        return null;
    }

    return (
        <VpaTimerWrapper onBack={onBack}>
            <UpiTimer
                countdownTimeInS={pollingDurationInMs / 1000}
                radius={TIMER_WIDGET_RADIUS_V4}
                strokeWidth={TIMER_WIDGET_STROKE_WIDTH_V4}
                showCancelModal={showCancelModal}
                setShowCancelModal={onClickCancel}
                onCancelTransaction={onCancelTransaction}
                paymentAmount={String(orderTotal)}
                itemCount={itemCount}
                startAt={elapsedTime / 1000}
            />
        </VpaTimerWrapper>
    );
}
